import { deleteAdminUserApi, forceChangePasswordApi, resendInvitationApi, useAdminUser } from "@/api/services/admin/admin-user";
import { useMe } from "@/api/services/admin/auth";
import { useEnum } from "@/api/services/global/enum";
import { IconSave } from "@/assets/icons/IconSave";
import { IconTrash } from "@/assets/icons/IconTrash";
import { Button } from "@/components/elements/Button";
import { DetailItem } from "@/components/elements/DetailItem";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { Modal } from "@/components/elements/Modal/Modal";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EAdminUserAccountStatus, EAdminUserStatus } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, state, text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { Card, CardContent, CardHeader, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

const AdminUserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteDialog = useDialog();
  const passwordResetDialog = useDialog();
  const { data, isLoading } = useAdminUser();
  const { data: me } = useMe();
  const { data: adminUserRoles } = useEnum("EAdminUserRole");
  const { data: adminUserStatuses } = useEnum("EAdminUserStatus");
  const { data: adminUserAccountStatuses } = useEnum("EAdminUserAccountStatus");

  const user = data?.data;
  const isCurrentUser = me?.data?.id?.toString() === id;

  const handleEdit = () => {
    navigate(getDynamicRoute(AppRoutes.systemSettingsAdminUserEdit, { id }));
  };

  const handleDelete = async () => {
    if (!id) return;
    const confirmed = await deleteDialog.show();
    if (!confirmed) return;
    try {
      await deleteAdminUserApi(Number(id));
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_LIST] });
      navigate(AppRoutes.systemSettingsAdminUsers);
    } catch (error) {
      showError(error);
    }
  };

  const isPasswordResetDisabled = useMemo(() => {
    if (!user?.isPasswordRegistered) return true;
    if (user?.status === EAdminUserStatus.DISABLED) return true;
    if (user?.accountStatus === EAdminUserAccountStatus.INVITATION_SENT || user?.accountStatus === EAdminUserAccountStatus.PASSWORD_RESET)
      return true;
  }, [user]);

  const handleForceChangePassword = async () => {
    if (!id) return;
    const confirmed = await passwordResetDialog.show();
    if (!confirmed) return;
    try {
      await forceChangePasswordApi({ adminUserId: Number(id) });
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL] });
    } catch (error) {
      showError(error);
    }
  };

  const handleResendInvitation = async () => {
    if (!id) return;
    try {
      await resendInvitationApi(Number(id));
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL] });
    } catch (error) {
      showError(error);
    }
  };

  const handleBack = () => {
    navigate(AppRoutes.systemSettingsAdminUsers);
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "ユーザーの状態",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  ユーザーの状態
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={isCurrentUser ? 5.8 : 4.8}>
                  <DetailItem required label="ステータス" value={findLabelByValue(adminUserStatuses, user?.status)} />
                </Grid>
                <Grid item xs={isCurrentUser ? 5.8 : 4.8}>
                  <DetailItem
                    label="認証ステータス"
                    value={findLabelByValue(
                      adminUserAccountStatuses,
                      user?.status === EAdminUserStatus.DISABLED ? EAdminUserAccountStatus.SUSPENDED : user?.accountStatus,
                    )}
                  />
                </Grid>
                {!isCurrentUser && (
                  <Grid
                    item
                    xs={2.4}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button size="sm" variant="ghost" disabled={isPasswordResetDisabled} onClick={handleForceChangePassword}>
                      パスワードリセット
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        ),
      },
      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <DetailItem required isInput label="ユーザー名" value={user?.fullName ?? ""} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem required isInput label="メールアドレス" value={user?.email ?? ""} />
                </Grid>
                <Grid item xs={6}>
                  <DetailItem required label="所属" value={findLabelByValue(adminUserRoles, user?.role)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
    ],
    [user, adminUserRoles, adminUserStatuses],
  );

  if (isLoading) return <LoadingPage />;

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Typography variant="head20Bold" color={text.primary}>
          管理ユーザー詳細
        </Typography>
        <Typography variant="body14" color={text.secondary}>
          いろはな管理ユーザーアカウントに関する情報を編集できます。
        </Typography>
      </Stack>

      <MenuNavigate
        contents={contents}
        contentAffix={
          <StickyFooter>
            <Stack width="100%" justifyContent="space-between" flexDirection="row">
              <Button size="sm" variant="ghost" onClick={handleBack}>
                戻る
              </Button>
              <Stack direction="row" gap={2}>
                <Button
                  size="sm"
                  variant="danger"
                  startIcon={<IconTrash fontSize="14px" color={isCurrentUser ? text.disable : state.error_3} />}
                  onClick={handleDelete}
                  disabled={isCurrentUser}
                >
                  削除
                </Button>
                {user?.accountStatus &&
                  user.accountStatus === EAdminUserAccountStatus.INVITATION_SENT &&
                  user?.status !== EAdminUserStatus.DISABLED && (
                    <Button size="sm" variant="outline" onClick={handleResendInvitation}>
                      再招待
                    </Button>
                  )}
                <Button size="sm" startIcon={<IconSave />} onClick={handleEdit}>
                  編集
                </Button>
              </Stack>
            </Stack>
          </StickyFooter>
        }
      />

      <Modal
        isOpen={deleteDialog.open}
        title="管理ユーザーの削除"
        labelOk="削除する"
        labelClose="キャンセル"
        onOk={deleteDialog.confirm}
        onClose={deleteDialog.cancel}
      >
        <Typography variant="cap12" color={text.secondary} py={2} component="div">
          この管理ユーザーを削除しますか？この操作は取り消せません。
        </Typography>
      </Modal>

      <Modal
        isOpen={passwordResetDialog.open}
        title="パスワードリセット"
        labelOk="リセットする"
        labelClose="キャンセル"
        onOk={passwordResetDialog.confirm}
        onClose={passwordResetDialog.cancel}
      >
        <Stack my={2}>
          <Typography variant="cap12" color={text.secondary} component="div">
            登録済みパスワードがリセットされます。再登録のためのURLが本人にメールで通知されます。
            <br />
            続行してよろしいですか。
          </Typography>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default AdminUserDetailPage;
